import React, { Suspense } from "react";
import { lazyWithRetry } from "@/lib/lazyWithRetry";
import { JsonLd } from "@/components/seo/JsonLd";
import { ORGANIZATION_ID, WEBSITE_ID, organizationRef } from "@/components/seo/entityGraph";
import { Seo } from "@/components/seo/Seo";
import { useSocialLinks } from "@/hooks/useSocialLinks";
import { MarketingHeader } from "@/components/marketing/MarketingHeader";
import { HeroSection } from "@/components/marketing/HeroSection";
import { StickyMobileCTA } from "@/components/marketing/StickyMobileCTA";
import { SectionDivider } from "@/components/marketing/SectionDivider";

const FeaturedInSection = lazyWithRetry(() => import("@/components/marketing/FeaturedInSection").then(m => ({ default: m.FeaturedInSection })));
const FeaturedPodcastersSection = lazyWithRetry(() => import("@/components/marketing/FeaturedPodcastersSection").then(m => ({ default: m.FeaturedPodcastersSection })));
const ValuePropsSection = lazyWithRetry(() => import("@/components/marketing/ValuePropsSection").then(m => ({ default: m.ValuePropsSection })));
const StatsSection = lazyWithRetry(() => import("@/components/marketing/StatsSection").then(m => ({ default: m.StatsSection })));
const HowItWorksSection = lazyWithRetry(() => import("@/components/marketing/HowItWorksSection").then(m => ({ default: m.HowItWorksSection })));
const FeaturesSection = lazyWithRetry(() => import("@/components/marketing/FeaturesSection").then(m => ({ default: m.FeaturesSection })));
const PricingPreview = lazyWithRetry(() => import("@/components/marketing/PricingPreview").then(m => ({ default: m.PricingPreview })));
const TestimonialsSection = lazyWithRetry(() => import("@/components/marketing/TestimonialsSection").then(m => ({ default: m.TestimonialsSection })));
const FinalCTASection = lazyWithRetry(() => import("@/components/marketing/FinalCTASection").then(m => ({ default: m.FinalCTASection })));
const MarketingFooter = lazyWithRetry(() => import("@/components/marketing/MarketingFooter").then(m => ({ default: m.MarketingFooter })));

const Index = () => {
  // Same admin-managed source the footer renders, so the two never drift.
  const { urls: socialUrls } = useSocialLinks();

  return (
    <div className="min-h-screen">
      <Seo
        title="PODGO | Your Podcast. Everywhere."
        description="Your podcast, distributed everywhere. Upload once and reach every listening platform with PODGO's unified podcast operations platform."
        url="/"
      />
      <JsonLd
        id="home"
        data={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              "@id": ORGANIZATION_ID,
              name: "PODGO",
              legalName: "PODGO, LLC",
              url: "https://podgo.io/",
              description:
                "PODGO is a podcast operations platform. Podcasters upload once and PODGO hosts the audio, manages the RSS feed, distributes to every major listening platform, reports downloads and revenue, sells advertising against the show, and books studio time.",
              email: "support@podgo.io",
              logo: {
                "@type": "ImageObject",
                "@id": "https://podgo.io/#logo",
                url: "https://podgo.io/logo-512.png",
                contentUrl: "https://podgo.io/logo-512.png",
                width: 512,
                height: 512,
                caption: "PODGO",
              },
              image: { "@id": "https://podgo.io/#logo" },
              contactPoint: [
                {
                  "@type": "ContactPoint",
                  contactType: "Customer Service",
                  email: "support@podgo.io",
                  availableLanguage: "en",
                },
              ],
              // Same admin-managed profiles the footer renders.
              sameAs: socialUrls,
            },
            {
              "@type": "WebSite",
              "@id": WEBSITE_ID,
              name: "PODGO",
              alternateName: "PODGO Podcast Platform",
              url: "https://podgo.io/",
              inLanguage: "en",
              publisher: organizationRef,
            },
          ],
        }}
      />
      <MarketingHeader />
      <main id="main-content">
        <HeroSection />
        <Suspense fallback={null}>
          <FeaturedInSection />
          <FeaturedPodcastersSection />
          <SectionDivider className="bg-primary" variant="subtle" />
          <ValuePropsSection />
          <SectionDivider className="bg-primary" variant="accent" />
          <StatsSection />
          <HowItWorksSection />
          <SectionDivider className="bg-secondary" variant="accent" />
          <FeaturesSection />
          <PricingPreview />
          <TestimonialsSection />
          <FinalCTASection />
        </Suspense>
      </main>
      <Suspense fallback={null}>
        <MarketingFooter />
      </Suspense>
      <StickyMobileCTA />
    </div>
  );
};

export default Index;